import { Injectable } from '@angular/core';
import { AsYouType, CountryCode, E164Number, isPossiblePhoneNumber, isSupportedCountry, isValidPhoneNumber, parsePhoneNumberFromString, PhoneNumber } from 'libphonenumber-js';
import { distinctUntilChanged } from 'rxjs/operators';
import { StudioConfigService } from './studio-config.service';

export interface PhoneValidationResult {
  e164?: E164Number;
  national?: string;
  formattedInternational?: string;
  country?: CountryCode;
  isValid: boolean;
  isPossible: boolean;
  errors?: string[];
}

@Injectable({
  providedIn: 'root'
})
export class PhoneNumberService {
  private fallbackCountry: CountryCode = 'US';

  constructor(private studioConfigService: StudioConfigService) {
    // Follow the selected studio's country so numbers stored without a leading
    // '+' are parsed against the right dialling plan (e.g. an Italian studio's
    // '393382730453' is +39 338 273 0453, not a 12-digit US number).
    this.studioConfigService
      .getCountryCode()
      .pipe(distinctUntilChanged())
      .subscribe(code => {
        const normalized = (code || '').toUpperCase();
        if (isSupportedCountry(normalized)) {
          this.fallbackCountry = normalized as CountryCode;
        }
      });
  }

  setFallbackCountry(country: CountryCode) {
    this.fallbackCountry = country;
  }

  getFallbackCountry(): CountryCode {
    return this.fallbackCountry;
  }

  parseNumber(raw: string | null | undefined, explicitCountry?: CountryCode): PhoneValidationResult {
    const result: PhoneValidationResult = {
      isValid: false,
      isPossible: false,
      errors: []
    };

    if (!raw) {
      result.errors?.push('empty');
      return result;
    }

    const trimmed = raw.toString().trim();
    const defaultCountry = explicitCountry || this.fallbackCountry;

    try {
      const parsed: PhoneNumber | undefined = parsePhoneNumberFromString(trimmed, defaultCountry);
      if (!parsed) {
        result.errors?.push('unparseable');
        return result;
      }

      result.country = parsed.country as CountryCode | undefined;
      result.e164 = parsed.number as E164Number;
      result.national = parsed.formatNational();
      result.formattedInternational = parsed.formatInternational();
      result.isValid = parsed.isValid();
      result.isPossible = parsed.isPossible();
    } catch (error) {
      result.errors?.push('exception');
    }

    if (!result.isValid) {
      result.isPossible = isPossiblePhoneNumber(trimmed, defaultCountry);
      if (!result.isPossible) {
        result.errors?.push('impossible');
      }
    }

    return result;
  }

  formatForInput(value: string, country?: CountryCode): string {
    if (!value) {
      return '';
    }

    const formatter = new AsYouType(country || this.fallbackCountry);
    return formatter.input(value);
  }

  isValid(raw: string | null | undefined, country?: CountryCode): boolean {
    if (!raw) {
      return false;
    }

    return isValidPhoneNumber(raw, country || this.fallbackCountry);
  }
}
